import { useEffect, useState, useCallback, useRef } from "react";
import { useAudioAnalyzer } from "../hooks/useAudioAnalyzer";
import type { AudioSource } from "../hooks/useAudioAnalyzer";
import { DebugPanel } from "./DebugPanel";
import { GlobalSettingsPanel } from "./GlobalSettingsPanel";
import { LookAheadControl } from "./LookAheadControl";
import { CornersOutIcon } from "@phosphor-icons/react/dist/csr/CornersOut";
import { CornersInIcon } from "@phosphor-icons/react/dist/csr/CornersIn";
import { HighFreqDebugPanel } from "./HighFreqDebugPanel";
import { FpsPanel } from "./FpsPanel";
import { ThreeLightStage } from "./ThreeLightStage";
import type { IntensityRef, HighFreqIntensityRef } from "../App";
import { PerformanceControls } from "./PerformanceControls";
import type { ReactingMode } from "../visuals/lightFrameEngine";
import { performanceKey, isEditingTarget } from "../visuals/performanceKeys";

export interface MultiLayerVisualizerProps {
  audioSource: AudioSource; // Audio source (file or system stream)
  showDebug?: boolean;
  onHideControls?: () => void;
  onToggleControls?: () => void;
  threshold?: number;
  minFreq?: number;
  maxFreq?: number;
  lookAhead?: number;
  onThresholdChange?: (threshold: number) => void;
  onMinFreqChange?: (minFreq: number) => void;
  onMaxFreqChange?: (maxFreq: number) => void;
  onLookAheadChange?: (lookAhead: number) => void;
  intensityRef?: React.RefObject<IntensityRef>;
  // High frequency (laser) props
  highFreqMin?: number;
  highFreqMax?: number;
  onHighFreqMinChange?: (minFreq: number) => void;
  onHighFreqMaxChange?: (maxFreq: number) => void;
  highFreqIntensityRef?: React.RefObject<HighFreqIntensityRef>;
  // Back to upload callback
  onBackToUpload?: () => void;
  reactingMode?: ReactingMode;
  rotationEnabled?: boolean;
  onModeChange?: (mode: ReactingMode) => void;
  onRotationToggle?: () => void;
}

/** Web Audio transport and DOM controls surrounding the Three.js light stage. */
export function MultiLayerVisualizer({
  audioSource,
  showDebug = false,
  onHideControls,
  onToggleControls,
  threshold = 0.3,
  minFreq = 20,
  maxFreq = 100,
  lookAhead = 0,
  onThresholdChange,
  onMinFreqChange,
  onMaxFreqChange,
  onLookAheadChange,
  intensityRef,
  // High frequency props
  highFreqMin = 4000,
  highFreqMax = 12000,
  onHighFreqMinChange,
  onHighFreqMaxChange,
  highFreqIntensityRef,
  onBackToUpload,
  reactingMode = 1,
  rotationEnabled = true,
  onModeChange,
  onRotationToggle,
}: MultiLayerVisualizerProps) {
  const audioAnalyzer = useAudioAnalyzer({ fftSize: 8192, audioSource, lookAhead });
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Determine if we're in system audio mode and if lookAhead is supported
  const isLiveAudio = audioSource.type !== "file";
  const supportsLookAhead = audioSource.type === "file" || (audioSource.type === "system" && audioSource.enableLookAhead === true);
  const supportsFullscreen = typeof document.documentElement.requestFullscreen === "function";
  const tapStart = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastTap = useRef<{ x: number; y: number; time: number } | null>(null);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!supportsFullscreen) { onHideControls?.(); return; }
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        onHideControls?.();
      });
    } else {
      document.exitFullscreen().catch((err) => {
        console.error("Failed to exit fullscreen:", err);
      });
    }
  }, [supportsFullscreen, onHideControls]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Update lookAhead in real-time without reinitializing audio
  useEffect(() => {
    audioAnalyzer.setLookAhead(lookAhead);
  }, [lookAhead]); // audioAnalyzer.setLookAhead is stable (useCallback)

  // Handle spacebar for play/pause
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const key = performanceKey(e, isEditingTarget(e.target));
      if (key === "f") {
        e.preventDefault();
        toggleFullscreen();
      }
      if (key === " " && !(e.target instanceof Element && e.target.closest("button, a"))) {
        e.preventDefault();
        if (audioAnalyzer.isPlaying) {
          audioAnalyzer.pause();
        } else {
          audioAnalyzer.play();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [audioAnalyzer, toggleFullscreen]);

  return (
    <div className={showDebug ? "visualizer" : "visualizer ui-hidden"}
      onPointerDown={event => {
        if (event.pointerType !== "touch" || !event.isPrimary || !(event.target instanceof Element) || !event.target.closest(".three-light-stage")) return;
        tapStart.current = { x: event.clientX, y: event.clientY, time: performance.now() };
      }}
      onPointerCancel={() => { tapStart.current = null; lastTap.current = null; }}
      onPointerUp={event => {
        if (event.pointerType !== "touch" || !event.isPrimary) return;
        const start = tapStart.current; tapStart.current = null;
        const now = performance.now();
        if (!start || now - start.time > 300 || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 20) { lastTap.current = null; return; }
        const last = lastTap.current;
        if (last && now - last.time < 350 && Math.hypot(event.clientX - last.x, event.clientY - last.y) < 30) {
          lastTap.current = null; onToggleControls?.();
        } else lastTap.current = { x: event.clientX, y: event.clientY, time: now };
      }}>
      <ThreeLightStage
        mode={reactingMode} rotationEnabled={rotationEnabled} threshold={threshold}
        minFreq={20} maxFreq={20000} bassMin={minFreq} bassMax={maxFreq}
        highMin={highFreqMin} highMax={highFreqMax}
        playing={audioAnalyzer.isPlaying} sampleRate={audioAnalyzer.sampleRate}
        getFrequencyData={audioAnalyzer.getFrequencyData}
        onBassIntensityChange={(intensity, triggered) => {
          if (intensityRef?.current) { intensityRef.current.intensity = intensity; intensityRef.current.triggered = triggered; }
        }}
        onHighIntensityChange={(intensity, triggered) => {
          if (highFreqIntensityRef?.current) { highFreqIntensityRef.current.intensity = intensity; highFreqIntensityRef.current.triggered = triggered; }
        }}
      />

      {showDebug && (
        <aside className="instrument-panel" aria-label="演出控制面板">
          <header className="instrument-header">
            <div className="instrument-brand"><span>BASS</span><span>PERFORMANCE</span></div>
            <button className="text-button hide-controls" onClick={onHideControls} title="H · 隐藏所有面板和鼠标">
              <kbd>H</kbd><span>隐藏</span>
            </button>
          </header>
          <div className="instrument-scroll">
            <GlobalSettingsPanel
              isPlaying={audioAnalyzer.isPlaying} onPlay={audioAnalyzer.play} onPause={audioAnalyzer.pause}
              onStop={audioAnalyzer.stop} onSeek={audioAnalyzer.seek}
              getCurrentTime={audioAnalyzer.getCurrentTime} getDuration={audioAnalyzer.getDuration}
              onBackToUpload={onBackToUpload ?? (() => {})} audioSourceLabel={audioSource.label}
              isSystemAudio={isLiveAudio} isMicrophone={audioSource.type === "microphone"}
            />
            <p className="control-note touch-control-note">双击画面可显示 / 隐藏面板</p>
            {onModeChange && onRotationToggle && <PerformanceControls
              mode={reactingMode} rotationEnabled={rotationEnabled} showDebug={showDebug}
              onModeChange={onModeChange} onRotationToggle={onRotationToggle} />}
            {onThresholdChange && onMinFreqChange && onMaxFreqChange && intensityRef && <DebugPanel
              getFrequencyData={audioAnalyzer.getFrequencyData} sampleRate={audioAnalyzer.sampleRate} fftSize={8192}
              minFreq={minFreq} maxFreq={maxFreq} threshold={threshold} onThresholdChange={onThresholdChange}
              onMinFreqChange={onMinFreqChange} onMaxFreqChange={onMaxFreqChange} intensityRef={intensityRef}
            />}
            {onHighFreqMinChange && onHighFreqMaxChange && highFreqIntensityRef && <HighFreqDebugPanel
              getFrequencyData={audioAnalyzer.getFrequencyData} sampleRate={audioAnalyzer.sampleRate} fftSize={8192}
              minFreq={highFreqMin} maxFreq={highFreqMax}
              onMinFreqChange={onHighFreqMinChange} onMaxFreqChange={onHighFreqMaxChange}
              intensityRef={highFreqIntensityRef}
            />}
            {onLookAheadChange && <LookAheadControl value={lookAhead}
              supported={supportsLookAhead} onChange={onLookAheadChange} />}
          </div>
          <footer className="instrument-footer">
            <FpsPanel />
            <button className="text-button fullscreen-control" onClick={toggleFullscreen}
              aria-label={!supportsFullscreen ? "沉浸模式" : isFullscreen ? "Exit Fullscreen" : "Fullscreen"} title="F · 全屏">
              <kbd>F</kbd><span>{!supportsFullscreen ? "沉浸模式" : isFullscreen ? "退出全屏" : "全屏"}</span>
              {isFullscreen ? <CornersInIcon size={14} /> : <CornersOutIcon size={14} />}
            </button>
          </footer>
        </aside>
      )}

      {audioAnalyzer.isLoading && <div className="audio-notice" role="status">正在读取音频…</div>}
      {!audioAnalyzer.isLoading && audioAnalyzer.error && <div className="audio-notice" role="alert">
        <p>{audioAnalyzer.error}</p><button onClick={onBackToUpload}>重新选择音源</button>
      </div>}
      {!audioAnalyzer.isLoading && !audioAnalyzer.error && (audioAnalyzer.needsResume || (!isLiveAudio && !audioAnalyzer.isPlaying && !showDebug)) &&
        <div className="audio-notice"><button onClick={audioAnalyzer.play}>
          {isLiveAudio ? "恢复麦克风 / 音频输入" : "播放音乐"}
        </button></div>}
      {audioAnalyzer.inputMuted && !audioAnalyzer.needsResume && <div className="audio-notice" role="status">
        <p>音频输入被系统暂停，请返回前台，或重新连接音源。</p>
        <button onClick={onBackToUpload}>重新连接</button>
      </div>}

    </div>
  );
}
